import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const TRACKING_STORAGE_KEY = "zillout_exp_tracking";

export interface TrackingData {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  referrer: string | null;
  capturedAt: string;
}

/**
 * Hook to capture and store UTM parameters and referrer when user lands on experience site.
 * Data is stored in sessionStorage and used when redirecting to zillout.com for booking.
 */
export function useTrackingSource(): void {
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const utmSource = searchParams.get("utm_source");
    const utmMedium = searchParams.get("utm_medium");
    const utmCampaign = searchParams.get("utm_campaign");

    const hasUtmParams = utmSource || utmMedium || utmCampaign;
    const referrer = document.referrer || null;

    const existingData = sessionStorage.getItem(TRACKING_STORAGE_KEY);

    // If UTM params are present, always update (they take priority)
    if (hasUtmParams) {
      const trackingData: TrackingData = {
        utmSource,
        utmMedium,
        utmCampaign,
        referrer,
        capturedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(trackingData));
    } else if (!existingData && referrer) {
      // Only store referrer if no existing tracking data
      const trackingData: TrackingData = {
        utmSource: null,
        utmMedium: null,
        utmCampaign: null,
        referrer,
        capturedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(trackingData));
    }
  }, [location.search]);
}

/**
 * Get stored tracking data.
 */
export function getTrackingData(): TrackingData {
  const storedData = sessionStorage.getItem(TRACKING_STORAGE_KEY);

  if (storedData) {
    try {
      return JSON.parse(storedData) as TrackingData;
    } catch {
      // Invalid JSON, return defaults
    }
  }

  return {
    utmSource: null,
    utmMedium: null,
    utmCampaign: null,
    referrer: null,
    capturedAt: new Date().toISOString(),
  };
}

/**
 * Build URL query string with tracking parameters.
 * Appends UTM params to an existing URL.
 */
export function appendTrackingParams(baseUrl: string): string {
  const tracking = getTrackingData();
  const url = new URL(baseUrl);

  if (tracking.utmSource) {
    url.searchParams.set("utm_source", tracking.utmSource);
  }
  if (tracking.utmMedium) {
    url.searchParams.set("utm_medium", tracking.utmMedium);
  }
  if (tracking.utmCampaign) {
    url.searchParams.set("utm_campaign", tracking.utmCampaign);
  }
  if (tracking.referrer) {
    url.searchParams.set("referrer", tracking.referrer);
  }

  return url.toString();
}

/**
 * Clear tracking data
 */
export function clearTrackingData(): void {
  sessionStorage.removeItem(TRACKING_STORAGE_KEY);
}

